import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { getDate } from '@einhaus/meteoric-helpers';
import type {
    BarDuration,
    ChartPatternKey as ChartPatternKeyType,
    ChartPatternState as ChartPatternStateType
} from '@einhaus/project314-types';
import { trpc } from '@/api/trpc';
import { useAppQuery } from '@/hooks/useAppQuery';
import { showToast } from '@/components/Layout/Toast';
import { Button, Card, FormCheckbox, FormInput, FormSelect } from '@components/UI';
import {
    type CreateBatchFormState,
    type PrepareSessionFormState,
    defaultBatchForm,
    defaultPrepareSessionForm,
    patternKeyLabels,
    patternOptions,
    statusOptions,
    timeframeLabel,
    timframeOptions
} from './chartPatternAuditConstants';

type ChartPatternAuditSetupProps = {
    onStartReview: (batchId: number) => void;
};

const PREPARE_BATCH_LABELS = {
    currentPositives: 'Current Positives',
    recentFailures: 'Recent Failures',
    historicalSuccesses: 'Historical Successes',
    historicalFailures: 'Historical Failures'
} as const;

const ChartPatternAuditSetup = ({ onStartReview }: ChartPatternAuditSetupProps) => {
    const [prepareForm, setPrepareForm] = useState<PrepareSessionFormState>({ ...defaultPrepareSessionForm });
    const [batchForm, setBatchForm] = useState<CreateBatchFormState>({ ...defaultBatchForm });

    const batchesQuery = useAppQuery({
        ...trpc.chartPatternAudit.listBatches.queryOptions()
    });

    const prepareSessionMutation = useMutation({
        ...trpc.chartPatternAudit.prepareSession.mutationOptions({
            onSuccess: async (data) => {
                showToast(`Prepared ${data.preparedBatches.length} review batches`, 'success');
                await batchesQuery.refetch();
            }
        })
    });

    const createBatchMutation = useMutation({
        ...trpc.chartPatternAudit.createBatch.mutationOptions({
            onSuccess: async (data) => {
                showToast(`Batch #${data.batch.id} created with ${data.batch.totalItems} patterns`, 'success');
                await batchesQuery.refetch();
                onStartReview(data.batch.id);
            }
        })
    });

    const batches = useMemo(() => batchesQuery.data?.batches ?? [], [batchesQuery.data?.batches]);

    const batchesNewestFirst = useMemo(
        () =>
            [...batches].sort((left, right) => {
                if (left.createdAt === right.createdAt) {
                    return right.id - left.id;
                }

                return right.createdAt.localeCompare(left.createdAt);
            }),
        [batches]
    );

    const incompleteBatches = useMemo(
        () => batchesNewestFirst.filter((batch) => batch.reviewedItems < batch.totalItems),
        [batchesNewestFirst]
    );

    const nextIncompleteBatch = incompleteBatches[0] ?? null;
    const preparePatternOptions = useMemo(() => patternOptions.filter((option) => option.value !== ''), []);
    const prepareTimeframeOptions = useMemo(() => timframeOptions.filter((option) => option.value !== ''), []);

    const handlePrepareSession = async () => {
        const maxItemsPerBatch = Number(prepareForm.maxItemsPerBatch);
        const historicalLookbackDays = Number(prepareForm.historicalLookbackDays);
        const historicalMinQualityScore = Number(prepareForm.historicalMinQualityScore);

        if (!Number.isInteger(maxItemsPerBatch) || maxItemsPerBatch < 5) {
            showToast('Max items per batch must be an integer >= 5', 'error');
            return;
        }

        if (prepareForm.includeHistoricalBatches && (!Number.isInteger(historicalLookbackDays) || historicalLookbackDays < 30)) {
            showToast('Historical lookback days must be an integer >= 30', 'error');
            return;
        }

        if (
            prepareForm.includeHistoricalBatches &&
            (!Number.isFinite(historicalMinQualityScore) || historicalMinQualityScore < 0 || historicalMinQualityScore > 100)
        ) {
            showToast('Historical min quality score must be between 0 and 100', 'error');
            return;
        }

        try {
            await prepareSessionMutation.mutateAsync({
                patternKey: prepareForm.patternKey,
                barDuration: prepareForm.barDuration,
                maxItemsPerBatch,
                includeHistoricalBatches: prepareForm.includeHistoricalBatches,
                historicalLookbackDays: prepareForm.includeHistoricalBatches ? historicalLookbackDays : undefined,
                historicalMinQualityScore: prepareForm.includeHistoricalBatches ? historicalMinQualityScore : undefined
            });
        } catch (mutationError) {
            showToast(mutationError instanceof Error ? mutationError.message : 'Failed to prepare review session', 'error');
        }
    };

    const handleCreateBatch = async () => {
        if (!batchForm.name.trim()) {
            showToast('Enter a batch name', 'error');
            return;
        }

        try {
            await createBatchMutation.mutateAsync({
                name: batchForm.name.trim(),
                patternKey: batchForm.patternKey || null,
                barDuration: batchForm.barDuration || null,
                maxItems: Number(batchForm.maxItems),
                minQualityScore: batchForm.minQualityScore.trim() === '' ? null : Number(batchForm.minQualityScore),
                stateFilters: batchForm.state ? [batchForm.state] : [],
                onlySelectedOccurrences: batchForm.onlySelectedOccurrences,
                notes: batchForm.notes.trim() === '' ? null : batchForm.notes.trim()
            });
        } catch (mutationError) {
            showToast(mutationError instanceof Error ? mutationError.message : 'Failed to create batch', 'error');
        }
    };

    const latestPreparedSession = prepareSessionMutation.data ?? null;
    const batchesError = batchesQuery.error instanceof Error ? batchesQuery.error.message : null;

    return (
        <div className="max-w-6xl mx-auto space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-6">
                <div className="space-y-6">
                    <Card responsivePadding title="Prepare Review Session">
                        <div className="space-y-4">
                            <div className="text-sm text-zinc-400">
                                One action creates the standard review batches you actually need. Use this instead of the raw scripts unless
                                something looks wrong and needs engineering investigation.
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <FormSelect
                                    label="Pattern Type"
                                    options={preparePatternOptions}
                                    value={prepareForm.patternKey}
                                    onChange={(event) =>
                                        setPrepareForm((currentValue) => ({
                                            ...currentValue,
                                            patternKey: event.target.value as ChartPatternKeyType
                                        }))
                                    }
                                />
                                <FormSelect
                                    label="Timeframe"
                                    options={prepareTimeframeOptions}
                                    value={prepareForm.barDuration}
                                    onChange={(event) =>
                                        setPrepareForm((currentValue) => ({
                                            ...currentValue,
                                            barDuration: event.target.value as BarDuration
                                        }))
                                    }
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <FormInput
                                    inputMode="numeric"
                                    label="Items Per Batch"
                                    value={prepareForm.maxItemsPerBatch}
                                    onChange={(event) =>
                                        setPrepareForm((currentValue) => ({
                                            ...currentValue,
                                            maxItemsPerBatch: event.target.value
                                        }))
                                    }
                                />
                                <FormCheckbox
                                    checked={prepareForm.includeHistoricalBatches}
                                    label="Include historical spot-check batches"
                                    onChange={(event) =>
                                        setPrepareForm((currentValue) => ({
                                            ...currentValue,
                                            includeHistoricalBatches: event.target.checked
                                        }))
                                    }
                                />
                            </div>

                            {prepareForm.includeHistoricalBatches ? (
                                <div className="grid grid-cols-2 gap-3">
                                    <FormInput
                                        inputMode="numeric"
                                        label="Historical Lookback Days"
                                        value={prepareForm.historicalLookbackDays}
                                        onChange={(event) =>
                                            setPrepareForm((currentValue) => ({
                                                ...currentValue,
                                                historicalLookbackDays: event.target.value
                                            }))
                                        }
                                    />
                                    <FormInput
                                        inputMode="decimal"
                                        label="Historical Min Quality"
                                        value={prepareForm.historicalMinQualityScore}
                                        onChange={(event) =>
                                            setPrepareForm((currentValue) => ({
                                                ...currentValue,
                                                historicalMinQualityScore: event.target.value
                                            }))
                                        }
                                    />
                                </div>
                            ) : null}

                            <div className="rounded border border-zinc-800 bg-zinc-950 px-4 py-3 text-sm text-zinc-300 space-y-1">
                                <div className="font-medium text-white">This creates:</div>
                                <div>`Current Positives`: formed, triggered, successful</div>
                                <div>`Recent Failures`: failed patterns within the recent failure window</div>
                                {prepareForm.includeHistoricalBatches ? (
                                    <>
                                        <div>`Historical Successes`: occurrence-based winners across symbols</div>
                                        <div>`Historical Failures`: occurrence-based failures across symbols</div>
                                    </>
                                ) : null}
                            </div>

                            <Button fullWidth isLoading={prepareSessionMutation.isPending} variant="primary" onClick={handlePrepareSession}>
                                Prepare Review Session
                            </Button>
                        </div>
                    </Card>

                    {latestPreparedSession ? (
                        <Card responsivePadding title="Prepared Batches">
                            <div className="space-y-4">
                                <div className="text-sm text-zinc-400">
                                    Snapshot Date: <span className="text-zinc-200">{latestPreparedSession.snapshotDateTime}</span>
                                </div>

                                <div className="space-y-3">
                                    {latestPreparedSession.preparedBatches.map((preparedBatch) => (
                                        <div className="rounded border border-zinc-800 bg-zinc-950 p-3" key={preparedBatch.batch.id}>
                                            <div className="flex items-start justify-between gap-3">
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <div className="font-medium text-white">{preparedBatch.label}</div>
                                                        <div className="rounded bg-zinc-900 px-2 py-0.5 text-[11px] text-zinc-400">
                                                            #{preparedBatch.batch.id}
                                                        </div>
                                                    </div>
                                                    <div className="mt-1 text-sm text-zinc-400">{preparedBatch.description}</div>
                                                    <div className="mt-2 text-xs text-zinc-500">
                                                        {preparedBatch.batch.totalItems} items · {preparedBatch.batch.name}
                                                    </div>
                                                </div>
                                                <Button variant="secondary" onClick={() => onStartReview(preparedBatch.batch.id)}>
                                                    Review
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {latestPreparedSession.skippedPresets.length ? (
                                    <div className="rounded border border-yellow-900/50 bg-yellow-950/20 p-3 text-sm text-yellow-200 space-y-2">
                                        <div className="font-medium">Skipped Presets</div>
                                        {latestPreparedSession.skippedPresets.map((preset) => (
                                            <div key={preset.presetKey}>
                                                {PREPARE_BATCH_LABELS[preset.presetKey]}: {preset.reason}
                                            </div>
                                        ))}
                                    </div>
                                ) : null}
                            </div>
                        </Card>
                    ) : null}

                    <details className="rounded border border-zinc-800 bg-zinc-950">
                        <summary className="cursor-pointer list-none px-4 py-3 text-sm font-medium text-zinc-200">
                            Advanced: Create Custom Batch
                        </summary>

                        <div className="border-t border-zinc-800 px-4 py-4">
                            <div className="space-y-3">
                                <FormInput
                                    label="Batch Name"
                                    value={batchForm.name}
                                    onChange={(event) => setBatchForm((currentValue) => ({ ...currentValue, name: event.target.value }))}
                                />
                                <div className="grid grid-cols-2 gap-3">
                                    <FormSelect
                                        label="Pattern Type"
                                        options={patternOptions}
                                        value={batchForm.patternKey}
                                        onChange={(event) =>
                                            setBatchForm((currentValue) => ({
                                                ...currentValue,
                                                patternKey: event.target.value as ChartPatternKeyType | ''
                                            }))
                                        }
                                    />
                                    <FormSelect
                                        label="Timeframe"
                                        options={timframeOptions}
                                        value={batchForm.barDuration}
                                        onChange={(event) =>
                                            setBatchForm((currentValue) => ({
                                                ...currentValue,
                                                barDuration: event.target.value as BarDuration | ''
                                            }))
                                        }
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <FormInput
                                        inputMode="numeric"
                                        label="Max Items"
                                        value={batchForm.maxItems}
                                        onChange={(event) =>
                                            setBatchForm((currentValue) => ({ ...currentValue, maxItems: event.target.value }))
                                        }
                                    />
                                    <FormInput
                                        inputMode="decimal"
                                        label="Min Quality Score"
                                        value={batchForm.minQualityScore}
                                        onChange={(event) =>
                                            setBatchForm((currentValue) => ({
                                                ...currentValue,
                                                minQualityScore: event.target.value
                                            }))
                                        }
                                    />
                                </div>
                                <FormSelect
                                    label="Pattern Status"
                                    options={statusOptions}
                                    value={batchForm.state}
                                    onChange={(event) =>
                                        setBatchForm((currentValue) => ({
                                            ...currentValue,
                                            state: event.target.value as ChartPatternStateType | ''
                                        }))
                                    }
                                />
                                <FormCheckbox
                                    checked={batchForm.onlySelectedOccurrences}
                                    label="Only include the best match per symbol"
                                    onChange={(event) =>
                                        setBatchForm((currentValue) => ({
                                            ...currentValue,
                                            onlySelectedOccurrences: event.target.checked
                                        }))
                                    }
                                />
                                <div>
                                    <label className="block text-sm font-medium text-zinc-300 mb-1" htmlFor="batchNotes">
                                        Notes (optional)
                                    </label>
                                    <textarea
                                        className="w-full min-h-[80px] rounded-md bg-[#1E1E1E] border border-[#444] px-3 py-2 text-sm text-white focus:border-[#888] focus:outline-none"
                                        id="batchNotes"
                                        value={batchForm.notes}
                                        onChange={(event) =>
                                            setBatchForm((currentValue) => ({ ...currentValue, notes: event.target.value }))
                                        }
                                    />
                                </div>
                                <Button fullWidth isLoading={createBatchMutation.isPending} variant="secondary" onClick={handleCreateBatch}>
                                    Create Custom Batch
                                </Button>
                            </div>
                        </div>
                    </details>
                </div>

                <Card responsivePadding title="Existing Batches">
                    <div className="mb-4">
                        {nextIncompleteBatch ? (
                            <div className="rounded border border-blue-900/50 bg-blue-950/20 p-4">
                                <div className="flex items-start justify-between gap-4">
                                    <div>
                                        <div className="text-sm font-medium text-white">Resume Review</div>
                                        <div className="mt-1 text-sm text-zinc-300">
                                            Next incomplete batch: #{nextIncompleteBatch.id} {nextIncompleteBatch.name}
                                        </div>
                                        <div className="mt-2 text-xs text-zinc-400">
                                            {nextIncompleteBatch.reviewedItems}/{nextIncompleteBatch.totalItems} reviewed ·{' '}
                                            {nextIncompleteBatch.totalItems - nextIncompleteBatch.reviewedItems} remaining
                                            {incompleteBatches.length > 1 ? ` · ${incompleteBatches.length} incomplete batches total` : ''}
                                        </div>
                                    </div>
                                    <Button variant="primary" onClick={() => onStartReview(nextIncompleteBatch.id)}>
                                        Continue Review
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className="rounded border border-green-900/50 bg-green-950/20 p-4 text-sm text-green-200">
                                All audit batches are fully reviewed.
                            </div>
                        )}
                    </div>

                    {batchesError ? (
                        <div className="rounded border border-red-900/60 bg-red-950/20 p-3 text-sm text-red-200">{batchesError}</div>
                    ) : null}

                    {batchesQuery.isLoading ? (
                        <div className="text-zinc-400 text-sm">Loading batches…</div>
                    ) : batchesNewestFirst.length === 0 ? (
                        <div className="text-zinc-400 text-sm">No batches yet. Prepare a review session to get started.</div>
                    ) : (
                        <div className="space-y-2 max-h-[900px] overflow-y-auto">
                            {batchesNewestFirst.map((batch) => {
                                const progress = batch.totalItems > 0 ? (batch.reviewedItems / batch.totalItems) * 100 : 0;

                                return (
                                    <button
                                        className="w-full text-left rounded border border-zinc-800 bg-zinc-950 p-3 hover:border-zinc-600 transition-colors"
                                        key={batch.id}
                                        type="button"
                                        onClick={() => onStartReview(batch.id)}
                                    >
                                        <div className="flex items-center justify-between gap-3">
                                            <div className="flex min-w-0 items-center gap-2">
                                                <div className="rounded bg-zinc-900 px-2 py-0.5 text-[11px] text-zinc-400 shrink-0">
                                                    #{batch.id}
                                                </div>
                                                <div className="font-medium text-white text-sm truncate">{batch.name}</div>
                                            </div>
                                            <div className="text-xs text-zinc-500 shrink-0">
                                                {batch.reviewedItems}/{batch.totalItems}
                                            </div>
                                        </div>
                                        <div className="mt-1 flex items-center gap-2 text-xs text-zinc-400">
                                            <span>{patternKeyLabels[batch.patternKey ?? ''] ?? 'All patterns'}</span>
                                            <span className="text-zinc-600">·</span>
                                            <span>{timeframeLabel(batch.barDuration)}</span>
                                            <span className="text-zinc-600">·</span>
                                            <span>{getDate({ date: batch.createdAt, format: 'mdy' })}</span>
                                        </div>
                                        <div className="mt-2 h-1.5 rounded-full bg-zinc-800 overflow-hidden">
                                            <div
                                                className="h-full rounded-full bg-p314-blue transition-all"
                                                style={{ width: `${progress}%` }}
                                            />
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </Card>
            </div>
        </div>
    );
};

export default ChartPatternAuditSetup;
